import React, {FC, useState} from 'react'
import {
  validateImage,
  getImageDimensions,
  processImage,
  formatFileSize,
  getCompressionRatio,
} from 'utils/helpers/imageProcessor'

type Props = {
  handleSelectFile: any
}

const UploadFotoProfileContent: FC<Props> = ({handleSelectFile}) => {
  const [file, setFile] = useState<any>({})
  const [preview, setPreview] = useState<string>('')
  const [errors, setErrors] = useState<any>({})
  const [isProcessing, setIsProcessing] = useState(false)
  const [imageInfo, setImageInfo] = useState<any>({})

  const handleChangeFile = async (e: any) => {
    const selectedFile = e.target.files[0]
    if (!selectedFile) return

    setIsProcessing(true)
    setErrors({})
    setPreview('')
    setImageInfo({})

    try {
      // Validate file type and size
      const validation = validateImage(selectedFile)
      if (!validation.isValid) {
        setErrors({error: validation.error})
        setIsProcessing(false)
        return
      }

      // Validate dimensions
      const dimensionCheck = await getImageDimensions(selectedFile)
      if (!dimensionCheck.isValid) {
        setErrors({error: dimensionCheck.error})
        setIsProcessing(false)
        return
      }

      // Process image (resize + compress)
      const processed = await processImage(selectedFile)

      // Create File object from blob for upload
      const processedFile = new File([processed.file], selectedFile.name, {
        type: 'image/jpeg',
      })

      // Update UI
      setFile(processedFile)
      setPreview(processed.preview)
      handleSelectFile(processedFile)

      // Show image info
      const compressionRatio = getCompressionRatio(processed.originalSize, processed.size)
      setImageInfo({
        originalSize: formatFileSize(processed.originalSize),
        compressedSize: formatFileSize(processed.size),
        dimension: `${processed.width}×${processed.height}px`,
        compressionRatio,
        wasResized: processed.width !== dimensionCheck.width || processed.height !== dimensionCheck.height,
      })
    } catch (error: any) {
      setErrors({error: error.message || 'Gagal memproses foto. Coba foto lain.'})
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div>
      <div
        className='d-flex justify-content-between border rounded p-4'
        style={{cursor: 'pointer', backgroundColor: '#F4F7FE'}}
      >
        <label className='fw-bold' style={{cursor: 'pointer'}}>
          <i className='bi bi-upload text-black'></i> {isProcessing ? 'Sedang diproses...' : 'Upload Foto'}
          <input
            type='file'
            id='input-file-avatar'
            style={{display: 'none'}}
            name='GeneralImages'
            onChange={handleChangeFile}
            accept='.jpg,.jpeg,.png'
            disabled={isProcessing}
          />
        </label>
      </div>

      {/* Error Messages */}
      {errors.error && (
        <div className='alert alert-danger mt-3 mb-3' role='alert'>
          <i className='bi bi-exclamation-circle me-2'></i>
          {errors.error}
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className='mt-4 mb-3'>
          <p className='text-muted mb-2'>Preview:</p>
          <img
            src={preview}
            alt='Preview'
            className='rounded'
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              objectFit: 'cover',
              border: '1px solid #e0e0e0',
            }}
          />
        </div>
      )}

      {/* File Name */}
      {file?.name && (
        <p
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            marginTop: '12px',
            marginBottom: '12px',
          }}
          className='fw-semibold'
        >
          📄 {file?.name}
        </p>
      )}

      {/* Image Info */}
      {Object.keys(imageInfo).length > 0 && (
        <div className='alert alert-info mt-3 mb-3'>
          <div className='text-muted' style={{fontSize: '13px'}}>
            <p className='mb-2'>
              <strong>Ukuran Asli:</strong> {imageInfo.originalSize} → <strong>Compressed:</strong>{' '}
              {imageInfo.compressedSize}
              <span className='badge bg-success ms-2'>{imageInfo.compressionRatio}% lebih kecil</span>
            </p>
            <p className='mb-0'>
              <strong>Dimensi:</strong> {imageInfo.dimension}
              {imageInfo.wasResized && <span className='badge bg-info ms-2'>Sudah diresize</span>}
            </p>
          </div>
        </div>
      )}

      {/* Guidelines */}
      <div className='text-muted mt-3'>
        <p className='mb-2'>
          <strong>Ketentuan Foto:</strong>
        </p>
        <p className='mb-1'>📏 Minimal: 600 × 800 px (otomatis diresize jika lebih kecil)</p>
        <p className='mb-1'>📄 Format: .JPG atau .PNG</p>
        <p className='mb-1'>📦 Maksimal: 2 MB (otomatis dikompres)</p>
        <p className='mb-1'>✅ Background polos atau lingkungan profesional</p>
        <p className='mb-3'>❌ Hindari foto buram, filter berlebihan, atau foto grup</p>
        <p className='p-3 rounded' style={{backgroundColor: '#FFF3CD', borderLeft: '4px solid #FFC107'}}>
          💡 <strong>Tips:</strong> Gunakan pencahayaan natural dan pakaian formal agar profil Anda
          terlihat rapi di halaman Featured Alumni. Foto akan otomatis disesuaikan dengan ukuran dan
          kualitas terbaik.
        </p>
      </div>
    </div>
  )
}

const UploadFotoProfile: FC<Props> = ({handleSelectFile}) => {
  return <UploadFotoProfileContent handleSelectFile={handleSelectFile} />
}

export default UploadFotoProfile
